/**
 * LabCentersTab Component
 * Search and select lab centers for walk-in orders
 */

import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  MapPin,
  Phone,
  Clock,
  Search,
  Loader2,
  Navigation,
  ExternalLink,
} from 'lucide-react';
import { PatientLabOrder, LabCenter, searchCentersForOrder } from '@/services/patientLabService';
import { useToast } from '@/hooks/use-toast';

interface LabCentersTabProps {
  order: PatientLabOrder;
  sessionToken: string;
  onCenterSelected: () => void;
}

const LabCentersTab: React.FC<LabCentersTabProps> = ({
  order,
  sessionToken,
  onCenterSelected,
}) => {
  const { toast } = useToast();
  const [zipCode, setZipCode] = useState(
    order.patientAddress?.zip || ''
  );
  const [centers, setCenters] = useState<LabCenter[]>([]);
  const [loading, setLoading] = useState(false);
  const [radius, setRadius] = useState(25);

  const handleSearch = async () => {
    if (!zipCode || zipCode.length !== 5) {
      toast({
        title: 'Invalid Zip Code',
        description: 'Please enter a valid 5-digit zip code',
        variant: 'destructive',
      });
      return;
    }

    try {
      setLoading(true);
      const results = await searchCentersForOrder(
        order.id,
        zipCode,
        sessionToken,
        radius
      );
      setCenters(results);
      if (results.length === 0) {
        toast({
          title: 'No Centers Found',
          description: 'No lab centers found near this zip code',
        });
      }
    } catch (error: any) {
      console.error('Error searching centers:', error);
      toast({
        title: 'Search Failed',
        description:
          error.response?.data?.error?.message || 'Failed to search lab centers',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const getDirectionsUrl = (center: LabCenter) => {
    if (center.latitude && center.longitude) {
      return `https://www.google.com/maps/dir/?api=1&destination=${center.latitude},${center.longitude}`;
    }
    const address = `${center.address}, ${center.city}, ${center.state} ${center.zip}`;
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
  };

  return (
    <div className="space-y-4">
      {/* Search */}
      <Card className="border-slate-700 bg-slate-800">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Search className="h-5 w-5 text-blue-400" />
            Find Lab Centers
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-2">
            <div className="flex-1">
              <Label htmlFor="zipCode" className="text-slate-300">
                Zip Code
              </Label>
              <Input
                id="zipCode"
                value={zipCode}
                onChange={e => setZipCode(e.target.value)}
                placeholder="12345"
                maxLength={5}
                className="bg-slate-700 border-slate-600 text-white"
              />
            </div>
            <div className="w-32">
              <Label htmlFor="radius" className="text-slate-300">
                Radius (miles)
              </Label>
              <Input
                id="radius"
                type="number"
                value={radius}
                onChange={e => setRadius(parseInt(e.target.value, 10) || 25)}
                min={5}
                max={50}
                className="bg-slate-700 border-slate-600 text-white"
              />
            </div>
          </div>
          <Button
            onClick={handleSearch}
            disabled={loading || !zipCode}
            className="w-full"
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Searching...
              </>
            ) : (
              <>
                <Search className="h-4 w-4 mr-2" />
                Search
              </>
            )}
          </Button>
        </CardContent>
      </Card>

      {/* Results */}
      {centers.length > 0 && (
        <div className="space-y-3">
          <h3 className="text-lg font-medium text-white">
            Found {centers.length} center{centers.length !== 1 ? 's' : ''}
          </h3>
          {centers.map(center => (
            <Card
              key={center.id}
              className="border-slate-700 bg-slate-800 hover:bg-slate-750 transition-colors"
            >
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <h4 className="font-medium text-white mb-1">{center.name}</h4>
                    <div className="space-y-1 text-sm text-slate-300">
                      <div className="flex items-start gap-2">
                        <MapPin className="h-4 w-4 mt-0.5 flex-shrink-0" />
                        <div>
                          <p>{center.address}</p>
                          <p>
                            {center.city}, {center.state} {center.zip}
                          </p>
                        </div>
                      </div>
                      {center.phone && (
                        <div className="flex items-center gap-2">
                          <Phone className="h-4 w-4" />
                          <a
                            href={`tel:${center.phone}`}
                            className="text-blue-400 hover:text-blue-300"
                          >
                            {center.phone}
                          </a>
                        </div>
                      )}
                      {center.hours && (
                        <div className="flex items-start gap-2">
                          <Clock className="h-4 w-4 mt-0.5 flex-shrink-0" />
                          <span>{center.hours}</span>
                        </div>
                      )}
                      {center.distance && (
                        <div className="text-blue-400">
                          {center.distance.toFixed(1)} miles away
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-col gap-2">
                    <a
                      href={getDirectionsUrl(center)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-sm text-blue-400 hover:text-blue-300"
                    >
                      <Navigation className="h-4 w-4" />
                      Directions
                    </a>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {centers.length === 0 && !loading && zipCode && (
        <Card className="border-slate-700 bg-slate-800">
          <CardContent className="p-8 text-center">
            <MapPin className="h-12 w-12 text-slate-600 mx-auto mb-2" />
            <p className="text-slate-400">No centers found. Try a different zip code.</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default LabCentersTab;
